import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, getDocs, serverTimestamp, setDoc } from 'firebase/firestore';
import { AlertTriangle, BookOpen, Calendar, CheckCircle2, Clock, Loader2, RefreshCw, Save, Search } from 'lucide-react';
import { db } from '../../firebase/config';
import { getConfiguredSubjects, SEMESTER_MONTHS } from '../../utils/semesterConfig';

const EXAM_TASKS = [
  { key: 'iat1', label: 'IAT-1' },
  { key: 'iat2', label: 'IAT-2' }
];

const currentYear = new Date().getFullYear();

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const monthKey = (month) => String(month || '').replace(/[^a-z0-9]/gi, '_').toLowerCase();

const safeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const uniqueSorted = (values) => [...new Set(values.map((value) => String(value || '').trim()).filter(Boolean))]
  .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

const courseAttendanceDocId = ({ rollNumber, semester, subjectId, month, year }) => (
  `${rollNumber}_semester${semester}_${subjectId}_${year}_${monthKey(month)}`.replace(/[^a-z0-9_-]/gi, '_')
);

const marksDocId = (rollNumber, semester, subjectId) => (
  `${rollNumber}_semester${semester}_${subjectId}`.replace(/[^a-z0-9_-]/gi, '_')
);

const emptyDeadlineState = () => ({
  semester1: {
    attendance: Object.fromEntries(SEMESTER_MONTHS[1].map((month) => [month, ''])),
    iat1: '',
    iat2: ''
  },
  semester2: {
    attendance: Object.fromEntries(SEMESTER_MONTHS[2].map((month) => [month, ''])),
    iat1: '',
    iat2: ''
  }
});

const normalizeDeadlineState = (data = {}) => {
  const base = emptyDeadlineState();

  [1, 2].forEach((semester) => {
    const key = `semester${semester}`;
    const raw = data[key] || data[String(semester)] || {};
    const rawAttendance = raw.attendance || {};

    base[key] = {
      attendance: {
        ...base[key].attendance,
        ...Object.fromEntries(SEMESTER_MONTHS[semester].map((month) => [month, rawAttendance[month] || '']))
      },
      iat1: raw.iat1 || raw.iat1Deadline || '',
      iat2: raw.iat2 || raw.iat2Deadline || ''
    };
  });

  return base;
};

const hasExamMarks = (record, examKey) => {
  const marks = record?.[examKey]?.marks ?? record?.[`${examKey}Marks`];
  return marks !== undefined && marks !== null && marks !== '';
};

const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  if (typeof value.seconds === 'number') return value.seconds * 1000;

  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const deadlineEndMillis = (dateValue) => {
  if (!dateValue) return 0;
  const parsed = new Date(`${dateValue}T23:59:59`).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatDate = (dateValue) => {
  if (!dateValue) return 'Not set';
  const parsed = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return 'Not set';
  return parsed.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getCompletionStatus = (submitted, expected) => {
  if (expected === 0) return 'No students';
  if (submitted === 0) return 'Missing';
  if (submitted >= expected) return 'Complete';
  return 'Partial';
};

const getTimingStatus = ({ completionStatus, deadline, latestUpdated }) => {
  const due = deadlineEndMillis(deadline);
  if (!due) return completionStatus === 'Complete' ? 'Complete' : 'No deadline';

  if (completionStatus === 'Complete') {
    if (!latestUpdated) return 'Complete';
    return latestUpdated <= due ? 'On time' : 'Late';
  }

  return Date.now() > due ? 'Overdue' : 'Pending';
};

const getStatusClass = (status) => {
  if (status === 'Complete' || status === 'On time') return 'bg-green-50 text-green-700 border-green-200';
  if (status === 'Partial' || status === 'Pending' || status === 'Late') return 'bg-amber-50 text-amber-700 border-amber-200';
  if (status === 'Missing' || status === 'Overdue') return 'bg-red-50 text-red-700 border-red-200';
  return 'bg-gray-50 text-gray-600 border-gray-200';
};

const buildSubjectScopes = (subjects, students) => {
  const divisions = uniqueSorted(students.map((student) => student.division));

  return subjects.flatMap((subject) => {
    const subjectDivision = subject.teachingDivision || '';
    const divisionsForSubject = subjectDivision ? [subjectDivision] : (divisions.length ? divisions : ['']);

    return divisionsForSubject.map((division) => ({
      ...subject,
      scopeId: `${subject.id}:${division || 'all'}`,
      teachingDivision: division,
      scopeDivision: division
    }));
  });
};

const getScopeStudents = ({ students, userRole, assignedDivision, assignedBatch }) => (
  students.filter((student) => {
    if (userRole === 'classCoordinator' && assignedDivision) {
      return String(student.division || '') === String(assignedDivision);
    }

    if (userRole === 'mentor' && assignedBatch) {
      return String(student.batch || '') === String(assignedBatch);
    }

    return true;
  })
);

const getRowScopeStudents = (students, subject) => (
  students.filter((student) => (
    !subject.scopeDivision || String(student.division || '') === String(subject.scopeDivision)
  ))
);

const getAttendanceRecord = ({ attendanceById, attendanceFallback, rollNumber, semester, subject, month, year }) => {
  const directId = courseAttendanceDocId({
    rollNumber,
    semester,
    subjectId: subject.id,
    month,
    year
  });

  if (attendanceById[directId]) return attendanceById[directId];

  const fallbackKey = [
    rollNumber,
    Number(semester),
    subject.id,
    year,
    monthKey(month),
    subject.scopeDivision || ''
  ].join('|');

  return attendanceFallback[fallbackKey] || null;
};

const getMarksRecord = ({ marksById, marksFallback, rollNumber, semester, subject }) => {
  const directId = marksDocId(rollNumber, semester, subject.id);
  if (marksById[directId]) return marksById[directId];

  const fallbackKey = [
    rollNumber,
    Number(semester),
    subject.id,
    subject.scopeDivision || ''
  ].join('|');

  return marksFallback[fallbackKey] || null;
};

const buildPreviewRows = () => ([
  {
    id: 'preview-attendance-1',
    type: 'Attendance',
    period: 'August',
    subject: 'AM-I - Applied Mathematics-I',
    division: 'A',
    batchScope: '',
    incharge: 'Prof. Sample Mentor',
    expected: 62,
    submitted: 62,
    completionPercent: 100,
    completionStatus: 'Complete',
    timingStatus: 'On time',
    deadline: '2026-08-31',
    missingText: '-'
  },
  {
    id: 'preview-attendance-2',
    type: 'Attendance',
    period: 'September',
    subject: 'AP - Applied Physics',
    division: 'B',
    batchScope: '',
    incharge: 'Prof. Sample Faculty',
    expected: 58,
    submitted: 41,
    completionPercent: 70.69,
    completionStatus: 'Partial',
    timingStatus: 'Overdue',
    deadline: '2026-09-30',
    missingText: '17 pending'
  },
  {
    id: 'preview-iat1',
    type: 'IAT-1',
    period: 'IAT-1',
    subject: 'BEE - Basic Electrical and Electronics Engineering',
    division: 'A',
    batchScope: '',
    incharge: 'Prof. Sample Faculty',
    expected: 62,
    submitted: 0,
    completionPercent: 0,
    completionStatus: 'Missing',
    timingStatus: 'Pending',
    deadline: '',
    missingText: '62 pending'
  }
]);

const SubmissionStatusWidget = ({
  userRole = 'hod',
  assignedDivision = '',
  assignedBatch = '',
  userName = '',
  preview = false
}) => {
  const [semester, setSemester] = useState('1');
  const [year, setYear] = useState(currentYear);
  const [taskFilter, setTaskFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [marksRecords, setMarksRecords] = useState([]);
  const [deadlines, setDeadlines] = useState(emptyDeadlineState);
  const [deadlineDraft, setDeadlineDraft] = useState(emptyDeadlineState);
  const [loading, setLoading] = useState(!preview);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const semesterKey = `semester${semester}`;
  const months = SEMESTER_MONTHS[Number(semester)] || SEMESTER_MONTHS[1];
  const canEditDeadlines = userRole === 'hod';

  const loadData = useCallback(async () => {
    if (preview) return;

    try {
      setLoading(true);
      setError('');
      setMessage('');

      const [subjectsDoc, studentsSnap, attendanceSnap, marksSnap, deadlineDoc] = await Promise.all([
        getDoc(doc(db, 'semesterSubjects', `semester${semester}`)),
        getDocs(collection(db, 'students')),
        getDocs(collection(db, 'courseAttendance')),
        getDocs(collection(db, 'courseMarks')),
        getDoc(doc(db, 'academicDeadlines', 'courseCompletion'))
      ]);

      const nextDeadlines = normalizeDeadlineState(deadlineDoc.exists() ? deadlineDoc.data() : {});

      setSubjects(getConfiguredSubjects(subjectsDoc.exists() ? subjectsDoc.data() : null, Number(semester)));
      setStudents(studentsSnap.docs
        .map((studentDoc) => ({ id: studentDoc.id, ...studentDoc.data() }))
        .sort((a, b) => String(a.rollNumber || '').localeCompare(String(b.rollNumber || ''), undefined, { numeric: true })));
      setAttendanceRecords(attendanceSnap.docs.map((attendanceDoc) => ({ id: attendanceDoc.id, ...attendanceDoc.data() })));
      setMarksRecords(marksSnap.docs.map((marksDoc) => ({ id: marksDoc.id, ...marksDoc.data() })));
      setDeadlines(nextDeadlines);
      setDeadlineDraft(nextDeadlines);
    } catch (loadError) {
      console.error('Error loading submission status:', loadError);
      setError('Could not load completion report. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [preview, semester]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const reportRows = useMemo(() => {
    if (preview) return buildPreviewRows();

    const scopedStudents = getScopeStudents({ students, userRole, assignedDivision, assignedBatch });
    const subjectScopes = buildSubjectScopes(subjects, scopedStudents.length ? scopedStudents : students);
    const scopedSubjectTasks = subjectScopes.filter((subject) => {
      if (userRole === 'classCoordinator' && assignedDivision) {
        return !subject.scopeDivision || String(subject.scopeDivision) === String(assignedDivision);
      }

      if (userRole === 'mentor' && assignedBatch) {
        return getRowScopeStudents(scopedStudents, subject).length > 0;
      }

      return true;
    });

    const attendanceById = {};
    const attendanceFallback = {};
    attendanceRecords.forEach((record) => {
      attendanceById[record.id] = record;
      const key = [
        record.rollNumber,
        Number(record.semester),
        record.subjectId,
        record.year || year,
        monthKey(record.month),
        record.teachingDivision || record.division || ''
      ].join('|');
      attendanceFallback[key] = record;
    });

    const marksById = {};
    const marksFallback = {};
    marksRecords.forEach((record) => {
      marksById[record.id] = record;
      const key = [
        record.rollNumber,
        Number(record.semester),
        record.subjectId,
        record.teachingDivision || record.division || ''
      ].join('|');
      marksFallback[key] = record;
    });

    const nextRows = [];

    scopedSubjectTasks.forEach((subject) => {
      const studentsForSubject = getRowScopeStudents(scopedStudents, subject);
      const subjectLabel = `${subject.code ? `${subject.code} - ` : ''}${subject.name || 'Subject'}`;
      const division = subject.scopeDivision || 'All';
      const incharge = subject.courseInchargeName || subject.courseInchargeEmail || 'Unassigned';

      months.forEach((month) => {
        const matchingRecords = studentsForSubject
          .map((student) => getAttendanceRecord({
            attendanceById,
            attendanceFallback,
            rollNumber: student.rollNumber,
            semester,
            subject,
            month,
            year
          }))
          .filter(Boolean);
        const submitted = matchingRecords.length;
        const expected = studentsForSubject.length;
        const latestUpdated = matchingRecords.reduce((latest, record) => Math.max(latest, toMillis(record.updatedAt)), 0);
        const completionStatus = getCompletionStatus(submitted, expected);
        const deadline = deadlines[semesterKey]?.attendance?.[month] || '';
        const timingStatus = getTimingStatus({ completionStatus, deadline, latestUpdated });

        nextRows.push({
          id: `attendance:${subject.scopeId}:${month}`,
          type: 'Attendance',
          typeKey: 'attendance',
          period: month,
          subject: subjectLabel,
          division,
          batchScope: userRole === 'mentor' && assignedBatch ? assignedBatch : '',
          incharge,
          expected,
          submitted,
          completionPercent: expected ? Number(((submitted / expected) * 100).toFixed(2)) : 0,
          completionStatus,
          timingStatus,
          deadline,
          missingText: expected - submitted > 0 ? `${expected - submitted} pending` : '-'
        });
      });

      EXAM_TASKS.forEach((exam) => {
        const matchingRecords = studentsForSubject
          .map((student) => getMarksRecord({
            marksById,
            marksFallback,
            rollNumber: student.rollNumber,
            semester,
            subject
          }))
          .filter((record) => hasExamMarks(record, exam.key));
        const submitted = matchingRecords.length;
        const expected = studentsForSubject.length;
        const latestUpdated = matchingRecords.reduce((latest, record) => Math.max(latest, toMillis(record.updatedAt)), 0);
        const completionStatus = getCompletionStatus(submitted, expected);
        const deadline = deadlines[semesterKey]?.[exam.key] || '';
        const timingStatus = getTimingStatus({ completionStatus, deadline, latestUpdated });

        nextRows.push({
          id: `${exam.key}:${subject.scopeId}`,
          type: exam.label,
          typeKey: exam.key,
          period: exam.label,
          subject: subjectLabel,
          division,
          batchScope: userRole === 'mentor' && assignedBatch ? assignedBatch : '',
          incharge,
          expected,
          submitted,
          completionPercent: expected ? Number(((submitted / expected) * 100).toFixed(2)) : 0,
          completionStatus,
          timingStatus,
          deadline,
          missingText: expected - submitted > 0 ? `${expected - submitted} pending` : '-'
        });
      });
    });

    return nextRows;
  }, [assignedBatch, assignedDivision, attendanceRecords, deadlines, marksRecords, months, preview, semester, semesterKey, students, subjects, userRole, year]);

  const filteredRows = useMemo(() => {
    const needle = normalizeText(search);

    return reportRows.filter((row) => {
      if (taskFilter !== 'all' && row.typeKey !== taskFilter) return false;
      if (!needle) return true;

      return [
        row.type,
        row.period,
        row.subject,
        row.division,
        row.batchScope,
        row.incharge,
        row.completionStatus,
        row.timingStatus
      ].some((value) => normalizeText(value).includes(needle));
    });
  }, [reportRows, search, taskFilter]);

  const summary = useMemo(() => {
    const expected = reportRows.reduce((sum, row) => sum + safeNumber(row.expected), 0);
    const submitted = reportRows.reduce((sum, row) => sum + safeNumber(row.submitted), 0);

    return {
      completion: expected ? Number(((submitted / expected) * 100).toFixed(2)) : 0,
      complete: reportRows.filter((row) => row.completionStatus === 'Complete').length,
      partial: reportRows.filter((row) => row.completionStatus === 'Partial').length,
      missing: reportRows.filter((row) => row.completionStatus === 'Missing').length,
      overdue: reportRows.filter((row) => row.timingStatus === 'Overdue').length
    };
  }, [reportRows]);

  const scopeLabel = useMemo(() => {
    if (userRole === 'classCoordinator') return assignedDivision ? `Division ${assignedDivision}` : 'Assigned division';
    if (userRole === 'mentor') return assignedBatch ? `Batch ${assignedBatch}` : 'Assigned batch';
    return 'All divisions';
  }, [assignedBatch, assignedDivision, userRole]);

  const updateDeadlineDraft = (path, value) => {
    setDeadlineDraft((current) => {
      const next = normalizeDeadlineState(current);
      const active = { ...next[semesterKey] };

      if (path.type === 'attendance') {
        active.attendance = {
          ...active.attendance,
          [path.month]: value
        };
      } else {
        active[path.type] = value;
      }

      return {
        ...next,
        [semesterKey]: active
      };
    });
  };

  const saveDeadlines = async () => {
    if (!canEditDeadlines) return;

    try {
      setSaving(true);
      setError('');
      setMessage('');

      await setDoc(doc(db, 'academicDeadlines', 'courseCompletion'), {
        ...deadlineDraft,
        updatedAt: serverTimestamp(),
        updatedBy: userName || 'HOD'
      }, { merge: true });

      setDeadlines(normalizeDeadlineState(deadlineDraft));
      setMessage('Deadlines saved for completion reports.');
    } catch (saveError) {
      console.error('Error saving deadlines:', saveError);
      setError('Could not save deadlines.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[260px] items-center justify-center text-gray-500">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Loading completion report
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
        <div className="flex flex-wrap items-center gap-2 font-semibold">
          <BookOpen className="h-4 w-4" />
          Subject incharge work tracker
          <span className="rounded-full bg-white px-2 py-0.5 text-xs text-indigo-700 ring-1 ring-indigo-100">{scopeLabel}</span>
        </div>
        <p className="mt-1 text-xs text-indigo-800">
          Shows whether month-wise attendance and IAT marks have been filled for the students in this dashboard scope.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-12">
        <label className="lg:col-span-2">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Semester</span>
          <select
            value={semester}
            onChange={(event) => setSemester(event.target.value)}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          >
            <option value="1">Semester 1</option>
            <option value="2">Semester 2</option>
          </select>
        </label>

        <label className="lg:col-span-2">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Attendance Year</span>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength="4"
            value={year}
            onChange={(event) => setYear(event.target.value.replace(/\D/g, '').slice(0, 4))}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </label>

        <label className="lg:col-span-2">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Report</span>
          <select
            value={taskFilter}
            onChange={(event) => setTaskFilter(event.target.value)}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          >
            <option value="all">All work</option>
            <option value="attendance">Month-wise attendance</option>
            <option value="iat1">IAT-1 marks</option>
            <option value="iat2">IAT-2 marks</option>
          </select>
        </label>

        <label className="lg:col-span-4">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Search</span>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Subject, division, faculty, status"
              className="w-full rounded border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
        </label>

        <div className="flex items-end lg:col-span-2">
          <button
            type="button"
            onClick={loadData}
            disabled={preview}
            className="inline-flex w-full items-center justify-center rounded border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Reload
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        {[
          { label: 'Overall filled', value: `${summary.completion}%`, tone: 'text-indigo-700' },
          { label: 'Complete rows', value: summary.complete, tone: 'text-green-700' },
          { label: 'Partial rows', value: summary.partial, tone: 'text-amber-700' },
          { label: 'Missing rows', value: summary.missing, tone: 'text-red-700' },
          { label: 'Overdue rows', value: summary.overdue, tone: 'text-red-700' }
        ].map((item) => (
          <div key={item.label} className="rounded-lg border border-gray-200 bg-white px-3 py-3">
            <div className={`text-xl font-bold ${item.tone}`}>{item.value}</div>
            <div className="mt-1 text-xs font-semibold uppercase tracking-wide text-gray-500">{item.label}</div>
          </div>
        ))}
      </div>

      {canEditDeadlines ? (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="flex items-center text-sm font-semibold text-gray-900">
                <Calendar className="mr-2 h-4 w-4 text-indigo-600" />
                Deadline settings for Semester {semester}
              </div>
              <p className="mt-1 text-xs text-gray-500">These dates are used to mark Pending, Overdue, On time, or Late.</p>
            </div>
            <button
              type="button"
              onClick={saveDeadlines}
              disabled={saving}
              className="inline-flex items-center rounded bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:bg-gray-300"
            >
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Save Deadlines
            </button>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-7">
            {months.map((month) => (
              <label key={month} className="block">
                <span className="mb-1 block text-xs font-semibold text-gray-500">{month}</span>
                <input
                  type="date"
                  value={deadlineDraft[semesterKey]?.attendance?.[month] || ''}
                  onChange={(event) => updateDeadlineDraft({ type: 'attendance', month }, event.target.value)}
                  className="w-full rounded border border-gray-300 px-2 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </label>
            ))}
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-gray-500">IAT-1</span>
              <input
                type="date"
                value={deadlineDraft[semesterKey]?.iat1 || ''}
                onChange={(event) => updateDeadlineDraft({ type: 'iat1' }, event.target.value)}
                className="w-full rounded border border-gray-300 px-2 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-gray-500">IAT-2</span>
              <input
                type="date"
                value={deadlineDraft[semesterKey]?.iat2 || ''}
                onChange={(event) => updateDeadlineDraft({ type: 'iat2' }, event.target.value)}
                className="w-full rounded border border-gray-300 px-2 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </label>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
          <div className="mb-2 flex items-center text-sm font-semibold text-gray-900">
            <Clock className="mr-2 h-4 w-4 text-indigo-600" />
            Current deadlines
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            {months.map((month) => (
              <span key={month} className="rounded-full border border-gray-200 bg-gray-50 px-2 py-1 text-gray-700">
                {month}: {formatDate(deadlines[semesterKey]?.attendance?.[month])}
              </span>
            ))}
            <span className="rounded-full border border-gray-200 bg-gray-50 px-2 py-1 text-gray-700">IAT-1: {formatDate(deadlines[semesterKey]?.iat1)}</span>
            <span className="rounded-full border border-gray-200 bg-gray-50 px-2 py-1 text-gray-700">IAT-2: {formatDate(deadlines[semesterKey]?.iat2)}</span>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertTriangle className="mr-2 h-4 w-4" />
          {error}
        </div>
      )}

      {message && (
        <div className="flex items-center rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
          <CheckCircle2 className="mr-2 h-4 w-4" />
          {message}
        </div>
      )}

      <div className="overflow-x-auto rounded border border-gray-200 bg-white">
        <table className="min-w-[1120px] w-full border-collapse text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="border-b border-r border-gray-200 px-3 py-2 text-left">Work</th>
              <th className="border-b border-r border-gray-200 px-3 py-2 text-left">Month/Test</th>
              <th className="border-b border-r border-gray-200 px-3 py-2 text-left">Subject</th>
              <th className="border-b border-r border-gray-200 px-3 py-2 text-left">Division</th>
              <th className="border-b border-r border-gray-200 px-3 py-2 text-left">Faculty</th>
              <th className="border-b border-r border-gray-200 px-3 py-2 text-center">Filled</th>
              <th className="border-b border-r border-gray-200 px-3 py-2 text-center">Completion</th>
              <th className="border-b border-r border-gray-200 px-3 py-2 text-left">Deadline</th>
              <th className="border-b border-r border-gray-200 px-3 py-2 text-left">Status</th>
              <th className="border-b border-gray-200 px-3 py-2 text-left">Pending</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 ? (
              <tr>
                <td colSpan="10" className="px-3 py-8 text-center text-gray-500">
                  No report rows match this scope.
                </td>
              </tr>
            ) : (
              filteredRows.map((row) => (
                <tr key={row.id} className="bg-white">
                  <td className="border-r border-t border-gray-200 px-3 py-2 font-semibold text-gray-900">{row.type}</td>
                  <td className="border-r border-t border-gray-200 px-3 py-2 text-gray-700">{row.period}</td>
                  <td className="border-r border-t border-gray-200 px-3 py-2 text-gray-800">{row.subject}</td>
                  <td className="border-r border-t border-gray-200 px-3 py-2 text-gray-700">
                    <div>{row.division === 'All' ? 'All divisions' : `Division ${row.division}`}</div>
                    {row.batchScope && <div className="text-xs text-gray-400">Batch {row.batchScope}</div>}
                  </td>
                  <td className="border-r border-t border-gray-200 px-3 py-2 text-gray-700">{row.incharge}</td>
                  <td className="border-r border-t border-gray-200 px-3 py-2 text-center font-semibold text-gray-800">
                    {row.submitted}/{row.expected}
                  </td>
                  <td className="border-r border-t border-gray-200 px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="h-2 flex-1 rounded-full bg-gray-100">
                        <div
                          className={`h-2 rounded-full ${row.completionPercent >= 100 ? 'bg-green-500' : row.completionPercent > 0 ? 'bg-amber-500' : 'bg-red-500'}`}
                          style={{ width: `${Math.min(100, row.completionPercent)}%` }}
                        />
                      </div>
                      <span className="w-12 text-right text-xs font-semibold text-gray-600">{row.completionPercent}%</span>
                    </div>
                  </td>
                  <td className="border-r border-t border-gray-200 px-3 py-2 text-gray-700">{formatDate(row.deadline)}</td>
                  <td className="border-r border-t border-gray-200 px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${getStatusClass(row.completionStatus)}`}>
                        {row.completionStatus}
                      </span>
                      <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${getStatusClass(row.timingStatus)}`}>
                        {row.timingStatus}
                      </span>
                    </div>
                  </td>
                  <td className="border-t border-gray-200 px-3 py-2 text-gray-700">{row.missingText}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SubmissionStatusWidget;
